import { useEffect, useState } from "react";
import { fetchPublicBoardSlates, type BoardSlate } from "./api";

// Board-slate catalog helper (ADR 0058). The whiteboard `board` value is either a
// built-in vector template (blank/football/…, drawn on the canvas — ADR 0045/0057)
// OR the id of an admin-uploaded image slate. Every board picker lists both, and
// every whiteboard render site resolves a `board` value to a background image url
// through the same shared catalog fetched here — so the resolution stays DRY.

/** The built-in vector board templates (ADR 0045/0057) — no image assets. */
export const BUILTIN_BOARD_OPTIONS: { value: string; label: string }[] = [
  { value: "blank", label: "Blank" },
  { value: "football", label: "Football field" },
  { value: "basketball", label: "Basketball court" },
  { value: "soccer", label: "Soccer pitch" },
  { value: "hockey", label: "Hockey rink" },
];

// Module-level cache so the public catalog is fetched once and shared across every
// render site (Director/Legend/guest/Fan whiteboards), not per-component.
let cache: BoardSlate[] | null = null;
let inflight: Promise<BoardSlate[]> | null = null;

/** Fetch (or reuse) the board-slate catalog. Best-effort — resolves to [] on error. */
export function loadBoardSlates(): Promise<BoardSlate[]> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetchPublicBoardSlates()
      .then((slates) => {
        cache = slates;
        return slates;
      })
      .catch(() => {
        cache = [];
        return [];
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Drop the cache so the next load refetches — call after an admin add/delete. */
export function invalidateBoardSlates(): void {
  cache = null;
  inflight = null;
}

/**
 * Fetch the uploaded board-slate catalog once (cached) for board pickers +
 * board→image resolution. Returns [] until loaded (and on error) so a whiteboard
 * always renders — an unresolved id simply falls back to the blank vector board.
 */
export function useBoardSlates(): BoardSlate[] {
  const [slates, setSlates] = useState<BoardSlate[]>(cache ?? []);
  useEffect(() => {
    let alive = true;
    void loadBoardSlates().then((s) => {
      if (alive) setSlates(s);
    });
    return () => {
      alive = false;
    };
  }, []);
  return slates;
}

/**
 * The background image url for a board value IFF it names an uploaded slate;
 * otherwise undefined (a built-in vector board, which WhiteboardCanvas draws).
 */
export function resolveBoardImageUrl(board: string | undefined, slates: BoardSlate[]): string | undefined {
  if (!board) return undefined;
  return slates.find((s) => s.id === board)?.url;
}

/** Board picker options = the built-in vector boards + the uploaded slates. */
export function boardOptions(slates: BoardSlate[]): { value: string; label: string }[] {
  return [...BUILTIN_BOARD_OPTIONS, ...slates.map((s) => ({ value: s.id, label: s.label }))];
}
